/* NH Dori tame (2026-10-01, front-end QA post-publish P1s). Theme-side only; nothing is changed in the Dori app.
   Dori Side Pal ("Ask Nova!") draws its launcher inside an open shadow root under #dori-react-widget-host. Its
   position:fixed wrapper stayed where the pill started (about 112px above the visible pill) and caught real taps on
   whatever sat under it, and the pill itself covered CTAs (hub "Check", PDP title, drawer Afterpay line, quick add).
   What this does:
   1. Inside Dori's shadow root: the launcher's wrapper never takes taps (pointer-events:none), only the button does.
      Selectors use Dori's stable class "fab-glass" and structure, never the hashed emotion classes.
   2. Dori's own floating launcher is switched off (doriWidget.hideLauncher() plus :host(.nh-dori-tamed) CSS), and the
      theme supplies the entry points instead, which open the same chat with doriWidget.open():
      - phones and tablets (<990px): an "Ask" tab in the bottom tab bar (snippets/nh-dock.liquid), so nothing floats
        over page content;
      - desktop, and landscape phones where the tab bar steps off: a compact launcher bottom right (.nhdt).
   3. The floating launcher is quiet (hidden) while the cart or menu drawer is open, while any Klaviyo popup is
      visible, and while the delivery checker input on the hub/place pages has focus.
   4. html.nh-dori-open is set while the chat panel is open; nh-kl-hush.js keeps Klaviyo popups hidden then.
   If Dori never loads, the entry points stay plain links to the contact page and Dori is untouched. */
(() => {
  'use strict';
  const root = document.documentElement;
  if (root.dataset.nhDoriTame) return;
  root.dataset.nhDoriTame = '1';

  const SHADOW_CSS = [
    'div:has(> div > button.fab-glass), div:has(> button.fab-glass) { pointer-events: none !important; }',
    'button.fab-glass { pointer-events: auto !important; }',
    ':host(.nh-dori-tamed) div:has(> div > button.fab-glass) { display: none !important; }',
  ].join('\n');
  const KL = '[data-testid="POPUP"], [data-testid="FLYOUT"], [data-testid="FULLSCREEN"], div.kl-private-reset-css-Xuajs1[role="dialog"]';
  const CHECKER = '.nhpl-chk input, .nhpl-chk__input';

  let shadowHost = null;
  let shadow = null;
  let ready = false;
  let isOpen = false;
  let opener = null;
  let openerByKey = false; // focus goes back to the entry point only for keyboard users (no stray ring after a tap)

  const widget = () => (window.doriWidget && typeof window.doriWidget.open === 'function' ? window.doriWidget : null);
  const findShadow = () => {
    const host = document.getElementById('dori-react-widget-host');
    if (!host) return null;
    if (host.shadowRoot) return host;
    for (const c of host.children) if (c.shadowRoot) return c;
    return null;
  };

  /* ---------- 1. shadow-root style (re-added if Dori ever drops it) ---------- */
  const ensureStyle = () => {
    if (!shadow || shadow.querySelector('style[data-nh-dori-tame]')) return;
    const s = document.createElement('style');
    s.setAttribute('data-nh-dori-tame', '');
    s.textContent = SHADOW_CSS;
    shadow.appendChild(s);
  };

  /* ---------- open state ---------- */
  // Open = the fixed panel that holds the chat's textarea takes taps (Dori switches it from pointer-events:none when
  // closed to auto when open, at the start of its slide transition). Fallback without such a panel: textarea on screen.
  const panelOpen = () => {
    if (!shadow) return false;
    const ta = shadow.querySelector('textarea');
    if (!ta) return false;
    for (let n = ta.parentElement; n; n = n.parentElement) {
      const cs = getComputedStyle(n);
      if (cs.position === 'fixed') return cs.pointerEvents !== 'none' && cs.visibility !== 'hidden' && cs.display !== 'none';
    }
    const r = ta.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.bottom > 0 && r.right > 0 && r.top < innerHeight && r.left < innerWidth;
  };
  const triggers = () => document.querySelectorAll('[data-nh-dori-open]');
  const syncOpen = () => {
    const open = panelOpen();
    if (open === isOpen) return;
    isOpen = open;
    root.classList.toggle('nh-dori-open', open);
    triggers().forEach((t) => t.setAttribute('aria-expanded', String(open)));
    document.dispatchEvent(new CustomEvent('nh:dori', { detail: { open } }));
    if (!open && openerByKey && opener && document.contains(opener)) {
      const a = document.activeElement;
      // give focus back to the entry point the shopper used, unless they have moved on
      if (!a || a === document.body || a === shadowHost || a.id === 'dori-react-widget-host') {
        const o = opener;
        // after this frame's class change (.nhdt is visibility:hidden while the chat is open)
        requestAnimationFrame(() => { if (o.getClientRects().length && getComputedStyle(o).visibility !== 'hidden') o.focus({ preventScroll: true }); });
      }
    }
  };
  let openQueued = false;
  let settleTimer = 0;
  const scheduleOpen = () => {
    if (openQueued) return;
    openQueued = true;
    requestAnimationFrame(() => { openQueued = false; ensureStyle(); syncOpen(); });
    // re-check once Dori's open/close transition has settled (transitions do not mutate the DOM)
    clearTimeout(settleTimer);
    settleTimer = setTimeout(syncOpen, 700);
  };

  /* ---------- 3. quiet states for the floating launcher ---------- */
  const klVisible = () => [...document.querySelectorAll(KL)].some((n) => {
    const r = n.getBoundingClientRect();
    if (r.width < 40 || r.height < 40) return false;
    const cs = getComputedStyle(n);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && +cs.opacity > 0.05;
  });
  const drawerOpen = () => !!document.querySelector('#CartDrawer.drawer--is-open, #NhDrawer.is-open, .drawer.drawer--is-open') ||
    root.classList.contains('js-drawer-open');
  const checkerFocused = () => { const a = document.activeElement; return !!(a && a.matches && a.matches(CHECKER)); };
  let quietQueued = false;
  const syncQuiet = () => {
    quietQueued = false;
    const quiet = drawerOpen() || klVisible() || checkerFocused();
    if (root.classList.contains('nh-dori-quiet') !== quiet) root.classList.toggle('nh-dori-quiet', quiet);
  };
  const scheduleQuiet = () => { if (quietQueued) return; quietQueued = true; requestAnimationFrame(syncQuiet); };

  document.addEventListener('focusin', (e) => { if (e.target.matches && e.target.matches(CHECKER)) scheduleQuiet(); });
  document.addEventListener('focusout', (e) => { if (e.target.matches && e.target.matches(CHECKER)) setTimeout(scheduleQuiet, 0); });
  window.addEventListener('klaviyoForms', () => { scheduleQuiet(); setTimeout(scheduleQuiet, 400); });
  const relevantNode = (n) => n.nodeType === 1 && (n.matches(KL) || /(^|\s)kl[-_]|klaviyo/i.test(n.className && n.className.toString ? n.className.toString() : '') || !!n.querySelector?.(KL));
  const watchBody = () => {
    new MutationObserver((ms) => {
      for (const m of ms) {
        if (m.type === 'attributes') {
          const t = m.target;
          if (t === root || (t.matches && t.matches('#CartDrawer, #NhDrawer, .drawer, ' + KL + ', .kl-private-reset-css-Xuajs1'))) { scheduleQuiet(); return; }
          continue;
        }
        if ([...m.addedNodes, ...m.removedNodes].some(relevantNode)) { scheduleQuiet(); return; }
        if (!shadowHost && [...m.addedNodes].some((n) => n.nodeType === 1 && (n.id === 'dori-react-widget-host' || n.querySelector?.('#dori-react-widget-host')))) tryAttach();
      }
    }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    new MutationObserver(scheduleQuiet).observe(root, { attributes: true, attributeFilter: ['class'] });
  };

  /* ---------- 2. take over the launcher once Dori is up ---------- */
  const tryAttach = () => {
    if (ready) return true;
    const sh = findShadow();
    if (sh && !shadow) {
      shadowHost = sh;
      shadow = sh.shadowRoot;
      shadowHost.classList.add('nh-dori-tamed'); // hides Dori's own launcher via :host(.nh-dori-tamed) at once
      ensureStyle();
      new MutationObserver(scheduleOpen).observe(shadow, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style'] });
    }
    const w = widget();
    if (!shadow || !w) return false;
    try { w.hideLauncher(); } catch (e) { /* the :host rule still hides it */ }
    ready = true;
    root.classList.add('nh-dori-ready');
    scheduleOpen();
    scheduleQuiet();
    return true;
  };

  // Dori loads from unpkg after the page; look for it cheaply for up to 20 s, then stop.
  const t0 = performance.now();
  const poll = () => {
    if (tryAttach()) return;
    if (performance.now() - t0 > 20000) {
      // never fully ready: if we hid Dori's own launcher, give it back so the chat stays reachable
      if (shadowHost && !ready) shadowHost.classList.remove('nh-dori-tamed');
      return;
    }
    setTimeout(poll, shadow ? 100 : 250);
  };

  /* ---------- entry points ---------- */
  const openChat = (w) => {
    try { w.open(); } catch (e) { return false; }
    setTimeout(scheduleOpen, 60);
    return true;
  };
  document.addEventListener('click', (e) => {
    const t = e.target.closest && e.target.closest('[data-nh-dori-open]');
    if (!t) return;
    const w = widget();
    if (!w && performance.now() - t0 > 20000) return; // Dori never came: follow the link (contact page)
    e.preventDefault();
    opener = t;
    openerByKey = e.detail === 0; // keyboard activation (Enter) has no click count
    if (w && ready) { if (isOpen) { try { w.close(); } catch (err) {} } else openChat(w); return; }
    // tapped before Dori finished loading: wait briefly, then open (or fall back to the link)
    t.setAttribute('aria-busy', 'true');
    const start = performance.now();
    const wait = () => {
      if (tryAttach() && widget()) { t.removeAttribute('aria-busy'); openChat(widget()); return; }
      if (performance.now() - start > 6000) { t.removeAttribute('aria-busy'); location.href = t.href; return; }
      setTimeout(wait, 100);
    };
    wait();
  });

  const start = () => { watchBody(); syncQuiet(); poll(); };
  if (document.body) start(); else document.addEventListener('DOMContentLoaded', start, { once: true });
})();
